import React from 'react';
import { FlexDiv, TextDefault } from './ui';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { optionDisplay, updateOptionAt } from './BlockRenderer';
import {
    getBlockOptionComponent,
    getOptionActionKey,
    getScoringFieldAndList,
    getScoreForBlockOption,
    removeBlockOptionScore,
    renameValueKeyInBlockAction,
    setBlockOptionComponent,
    setBlockValuesScore,
} from './questionActionsEditor';
import { ScoringComponentSelect } from './scoringComponentSelect';

const paperSx = {
    p: 2,
    mb: 2,
    borderRadius: 1,
    border: '1px solid',
    borderColor: 'divider',
    bgcolor: 'grey.50',
    boxShadow: 1,
};

function updateListEntryAtField(block, field, index, newText) {
    if (field === 'options') {
        return updateOptionAt(block.options || [], index, newText);
    }
    if (field === 'items') {
        const next = [...(block.items || [])];
        next[index] = newText;
        return next;
    }
    if (field === 'buttons') {
        const next = [...(block.buttons || [])];
        next[index] = newText;
        return next;
    }
    return block.options || [];
}

/**
 * Flat `actions[blockActionId].values` editor for options, orderable items, or button labels.
 */
export function FlatBlockScoresSection({
    selectedBlock,
    question,
    scoringComponentOptions,
    onPatchQuestion,
    onUpdateBlock,
}) {
    const spec = getScoringFieldAndList(selectedBlock);
    if (!spec || selectedBlock.type === 'conversation') {
        return null;
    }

    const { field, list } = spec;
    const minOpts = selectedBlock.type === 'radio' ? 2 : 1;

    return (
        <FlexDiv column gap={1}>
            <TextDefault bold>Choices and scores</TextDefault>
            <TextDefault lighter>
                Stored under question.actions for this block&apos;s actionId
                (values keyed by each choice value).
            </TextDefault>
            {list.map((option, index) => {
                const optKey = getOptionActionKey(option, index);
                const score = getScoreForBlockOption(
                    question.actions,
                    selectedBlock.actionId,
                    optKey
                );
                const scoreComponent =
                    selectedBlock.actionId && onPatchQuestion
                        ? getBlockOptionComponent(
                              question.actions,
                              selectedBlock.actionId,
                              optKey,
                              question
                          )
                        : 'behaviour';
                const canRemove = list.length > minOpts;
                const isObjectOption =
                    option != null &&
                    typeof option === 'object' &&
                    !Array.isArray(option);
                const showScoreKeyField =
                    isObjectOption &&
                    (field === 'options' || field === 'buttons') &&
                    selectedBlock.type !== 'numericalscale';
                return (
                    <Paper
                        key={`${selectedBlock.id}-${field}-${index}`}
                        variant="outlined"
                        sx={paperSx}
                    >
                        <Stack spacing={2}>
                            <TextField
                                size="small"
                                label="Choice label (text)"
                                value={optionDisplay(option)}
                                fullWidth
                                InputLabelProps={{ shrink: true }}
                                onChange={(e) => {
                                    if (!onPatchQuestion) {
                                        const next = updateListEntryAtField(
                                            selectedBlock,
                                            field,
                                            index,
                                            e.target.value
                                        );
                                        onUpdateBlock({ [field]: next });
                                        return;
                                    }
                                    onPatchQuestion((prev) => {
                                        const bid = selectedBlock.id;
                                        const block = prev.ui.blocks.find(
                                            (b) => b.id === bid
                                        );
                                        if (!block) return prev;
                                        const curList = block[field] || [];
                                        const oldKey = getOptionActionKey(
                                            curList[index],
                                            index
                                        );
                                        const newList = updateListEntryAtField(
                                            block,
                                            field,
                                            index,
                                            e.target.value
                                        );
                                        const newKey = getOptionActionKey(
                                            newList[index],
                                            index
                                        );
                                        let actions = prev.actions || {};
                                        if (
                                            field === 'options' &&
                                            block.actionId &&
                                            oldKey !== newKey &&
                                            !String(oldKey).startsWith('__idx_')
                                        ) {
                                            actions = renameValueKeyInBlockAction(
                                                actions,
                                                block.actionId,
                                                oldKey,
                                                newKey
                                            );
                                        }
                                        if (
                                            (field === 'items' ||
                                                field === 'buttons') &&
                                            block.actionId &&
                                            oldKey !== newKey &&
                                            !String(oldKey).startsWith('__idx_')
                                        ) {
                                            actions = renameValueKeyInBlockAction(
                                                actions,
                                                block.actionId,
                                                oldKey,
                                                newKey
                                            );
                                        }
                                        const blocks = prev.ui.blocks.map(
                                            (b) =>
                                                b.id === bid
                                                    ? { ...b, [field]: newList }
                                                    : b
                                        );
                                        return {
                                            ...prev,
                                            ui: { ...prev.ui, blocks },
                                            actions,
                                        };
                                    });
                                }}
                            />
                            {showScoreKeyField ? (
                                <TextField
                                    size="small"
                                    label="Score key (value)"
                                    fullWidth
                                    InputLabelProps={{ shrink: true }}
                                    value={String(option.value ?? '')}
                                    onChange={(e) => {
                                        if (!onPatchQuestion) return;
                                        const newKey = e.target.value;
                                        onPatchQuestion((prev) => {
                                            const bid = selectedBlock.id;
                                            const block = prev.ui.blocks.find(
                                                (b) => b.id === bid
                                            );
                                            if (!block?.actionId) return prev;
                                            const curList = block[field] || [];
                                            const oldKey = getOptionActionKey(
                                                curList[index],
                                                index
                                            );
                                            const prevRow = curList[index];
                                            const nextRow = {
                                                ...prevRow,
                                                value: newKey,
                                            };
                                            const newList = [...curList];
                                            newList[index] = nextRow;
                                            const newKeyResolved =
                                                getOptionActionKey(
                                                    nextRow,
                                                    index
                                                );
                                            let actions = prev.actions || {};
                                            if (
                                                block.actionId &&
                                                oldKey !== newKeyResolved &&
                                                !String(oldKey).startsWith(
                                                    '__idx_'
                                                )
                                            ) {
                                                actions =
                                                    renameValueKeyInBlockAction(
                                                        actions,
                                                        block.actionId,
                                                        oldKey,
                                                        newKeyResolved
                                                    );
                                            }
                                            const blocks = prev.ui.blocks.map(
                                                (b) =>
                                                    b.id === bid
                                                        ? {
                                                              ...b,
                                                              [field]: newList,
                                                          }
                                                        : b
                                            );
                                            return {
                                                ...prev,
                                                ui: { ...prev.ui, blocks },
                                                actions,
                                            };
                                        });
                                    }}
                                    helperText="Keys actions[block].values for scoring"
                                />
                            ) : null}
                            <Stack spacing={1.5} sx={{ width: '100%' }}>
                                <Stack
                                    direction={{ xs: 'column', sm: 'row' }}
                                    spacing={2}
                                    alignItems={{ xs: 'stretch', sm: 'flex-start' }}
                                    sx={{ width: '100%' }}
                                >
                                    <TextField
                                        label="Score"
                                        type="number"
                                        size="small"
                                        sx={{
                                            width: { xs: '100%', sm: 140 },
                                            flexShrink: 0,
                                        }}
                                        InputLabelProps={{ shrink: true }}
                                        value={score}
                                        inputProps={{ step: 'any' }}
                                        onChange={(e) => {
                                            if (
                                                !onPatchQuestion ||
                                                !selectedBlock.actionId
                                            ) {
                                                return;
                                            }
                                            const v = e.target.value;
                                            onPatchQuestion((prev) => {
                                                const bid = selectedBlock.id;
                                                const block =
                                                    prev.ui.blocks.find(
                                                        (b) => b.id === bid
                                                    );
                                                if (!block?.actionId)
                                                    return prev;
                                                const curList =
                                                    block[field] || [];
                                                const key = getOptionActionKey(
                                                    curList[index],
                                                    index
                                                );
                                                return {
                                                    ...prev,
                                                    actions: setBlockValuesScore(
                                                        prev.actions || {},
                                                        block.actionId,
                                                        key,
                                                        v,
                                                        prev
                                                    ),
                                                };
                                            });
                                        }}
                                    />
                                    {onPatchQuestion && selectedBlock.actionId ? (
                                        <Box
                                            sx={{
                                                flex: 1,
                                                minWidth: 0,
                                                width: { xs: '100%', sm: 'auto' },
                                            }}
                                        >
                                            <ScoringComponentSelect
                                                componentOptions={
                                                    scoringComponentOptions
                                                }
                                                value={scoreComponent}
                                                onChange={(comp) => {
                                                    onPatchQuestion((prev) => {
                                                        const bid =
                                                            selectedBlock.id;
                                                        const block =
                                                            prev.ui.blocks.find(
                                                                (b) => b.id === bid
                                                            );
                                                        if (!block?.actionId) {
                                                            return prev;
                                                        }
                                                        const curList =
                                                            block[field] || [];
                                                        const key =
                                                            getOptionActionKey(
                                                                curList[index],
                                                                index
                                                            );
                                                        return {
                                                            ...prev,
                                                            actions:
                                                                setBlockOptionComponent(
                                                                    prev.actions ||
                                                                        {},
                                                                    block.actionId,
                                                                    key,
                                                                    comp,
                                                                    prev
                                                                ),
                                                        };
                                                    });
                                                }}
                                            />
                                        </Box>
                                    ) : null}
                                </Stack>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        justifyContent: 'flex-end',
                                    }}
                                >
                                    <IconButton
                                        size="small"
                                        aria-label="Remove choice"
                                        disabled={!canRemove}
                                        sx={{ mt: 0 }}
                                        onClick={() => {
                                        if (!onPatchQuestion) return;
                                        onPatchQuestion((prev) => {
                                            const bid = selectedBlock.id;
                                            const block = prev.ui.blocks.find(
                                                (b) => b.id === bid
                                            );
                                            if (!block) return prev;
                                            const curList = block[field] || [];
                                            const key = getOptionActionKey(
                                                curList[index],
                                                index
                                            );
                                            const newList = curList.filter(
                                                (_, i) => i !== index
                                            );
                                            const nextActions =
                                                removeBlockOptionScore(
                                                    prev.actions || {},
                                                    block.actionId,
                                                    key
                                                );
                                            const blocks = prev.ui.blocks.map(
                                                (b) =>
                                                    b.id === bid
                                                        ? {
                                                              ...b,
                                                              [field]: newList,
                                                          }
                                                        : b
                                            );
                                            return {
                                                ...prev,
                                                ui: { ...prev.ui, blocks },
                                                actions: nextActions,
                                            };
                                        });
                                    }}
                                    >
                                        <DeleteOutlineIcon fontSize="small" />
                                    </IconButton>
                                </Box>
                            </Stack>
                        </Stack>
                    </Paper>
                );
            })}
            {onPatchQuestion && selectedBlock.actionId ? (
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1 }}>
                    <IconButton
                        size="small"
                        color="primary"
                        aria-label="Add choice"
                        onClick={() => {
                            onPatchQuestion((prev) => {
                                const bid = selectedBlock.id;
                                const block = prev.ui.blocks.find(
                                    (b) => b.id === bid
                                );
                                if (!block?.actionId) return prev;
                                const slug = `opt_${Date.now().toString(36)}`;
                                let newList;
                                if (field === 'options') {
                                    newList = [
                                        ...(block.options || []),
                                        {
                                            text: 'New option',
                                            value: slug,
                                        },
                                    ];
                                } else if (field === 'items') {
                                    newList = [...(block.items || []), slug];
                                } else {
                                    newList = [...(block.buttons || []), slug];
                                }
                                const nextActions = setBlockValuesScore(
                                    prev.actions || {},
                                    block.actionId,
                                    slug,
                                    0,
                                    prev
                                );
                                const blocks = prev.ui.blocks.map((b) =>
                                    b.id === bid ? { ...b, [field]: newList } : b
                                );
                                return {
                                    ...prev,
                                    ui: { ...prev.ui, blocks },
                                    actions: nextActions,
                                };
                            });
                        }}
                    >
                        <AddIcon fontSize="small" />
                    </IconButton>
                    <TextDefault lighter>Add choice</TextDefault>
                </Stack>
            ) : null}
        </FlexDiv>
    );
}
